import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { styled, css } from 'styled-components';
import type { FormikProps } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import { DataLakeQuery } from '@graylog/enterprise-api';

import SearchFeatureExplanation from 'data-lake/preview/SearchFeatureExplanation';
import DLLogViewWidget from 'data-lake/preview/DLLogViewWidget';
import type Query from 'views/logic/queries/Query';
import { Alert, Row } from 'components/bootstrap';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import Search from 'views/logic/search/Search';
import {
  execute,
  executeSearchJob,
  resetState as resetSearchExecutionState,
} from 'views/logic/slices/searchExecutionSlice';
import createSearch from 'views/logic/slices/createSearch';
import useIsLoading from 'views/hooks/useIsLoading';
import type {
  BaseLakeSearchType,
  ActivePreviewSearch,
  MostRecentJobIds,
  BaseSearchBarFormValues,
} from 'data-lake/preview/Types';
import {
  PAGE_SIZE,
  INITIAL_PAGE,
  PREVIEW_JOB_STATUS,
  HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT,
  HIDE_DL_SEARCH_SUCCESSFUL_SEARCH_ALERT,
  DATA_LAKE_PREVIEW_QUERY_KEY,
  VALID_INITIAL_SEARCH_STATUSES,
} from 'data-lake/preview/Constants';
import useWidgetResults from 'data-lake/preview/hooks/useWidgetResults';
import ProgressInformation from 'data-lake/preview/ProgressInformation';
import Delayed from 'components/common/Delayed';
import ExpiredSearchInfo from 'data-lake/preview/ExpiredSearchInfo';
import { selectJobIds } from 'views/logic/slices/searchExecutionSelectors';
import ExecutionInfo from 'views/components/views/ExecutionInfo';
import useJobProgress from 'data-lake/preview/hooks/useJobProgress';
import formatBackendMessages from 'data-lake/preview/utils/formatBackendMessages';
import type { ExtraArguments } from 'views/types';
import type { DLPreviewDispatch } from 'data-lake/preview/store/useDLPreviewDispatch';
import useDLPreviewDispatch from 'data-lake/preview/store/useDLPreviewDispatch';
import useDLPreviewSelector from 'data-lake/preview/store/useDLPreviewSelector';
import Store from 'logic/local-storage/Store';
import stopSearchResultsPolling from 'data-lake/preview/utils/stopSearchResultsPolling';
import { cancelJob } from 'views/logic/slices/executeJobResult';
import SuccessfulSearchInfo from 'data-lake/preview/SuccessfulSearchInfo';
import type FetchError from 'logic/errors/FetchError';
import UserNotification from 'util/UserNotification';
import IfFeatureEnabled from 'components/features/IfFeatureEnabled';
import DataLakeTypeSelect from 'data-lake/preview/DataLakeTypeSelect';
import useWidgetConfiguration from 'data-lake/preview/hooks/useWidgetConfiguration';

const WidgetContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
`;

const FullHeightRow = styled(Row)`
  flex: 1;
  overflow: auto;
  display: flex;
`;

const StyledRow = styled(Row)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.xs};
  `,
);

const StyledAlert = styled(Alert)`
  margin: 0;
`;

const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: ${theme.spacings.xs};
    gap: ${theme.spacings.sm};
    justify-content: space-between;
  `,
);

const LeftHeaderCol = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.lg};
  `,
);

const RightHeaderCol = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
  `,
);

const Headline = styled.h1(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.body};
  `,
);

const reexecuteSearchJob =
  (searchJobId: string, executingNodeId: string) =>
  (dispatch: DLPreviewDispatch, _getState, { searchExecutors }: ExtraArguments) =>
    dispatch(
      executeSearchJob({
        stopPolling: (progress: number) => stopSearchResultsPolling(INITIAL_PAGE, progress),
        searchExecutors,
        jobIds: {
          asyncSearchId: searchJobId,
          nodeId: executingNodeId,
        },
        page: INITIAL_PAGE,
        perPage: PAGE_SIZE,
      }),
    ).catch((error: FetchError) =>
      UserNotification.error(`Fetching search results failed with status: ${error.message}`),
    );

const usePollResultsForInitialSearchJob = (
  searchJobStatus: ActivePreviewSearch['status'] | undefined,
  searchJobProgress: number | undefined,
  searchJobId: string | undefined,
  executingNodeId: string | undefined,
) => {
  const dispatch = useDLPreviewDispatch();

  useEffect(() => {
    if (searchJobStatus === PREVIEW_JOB_STATUS.running && !stopSearchResultsPolling(INITIAL_PAGE, searchJobProgress)) {
      dispatch(reexecuteSearchJob(searchJobId, executingNodeId));
    }
  }, [dispatch, executingNodeId, searchJobId, searchJobProgress, searchJobStatus]);
};

const useMostRecentJobIds = (
  initialSearchJob: {
    status: ActivePreviewSearch['status'];
    id: string;
    nodeId: string;
  },
  initialSearchId: string | undefined,
  executedSearchId: string | undefined,
) => {
  const jobIds = useDLPreviewSelector(selectJobIds);

  const initialSearchJobId =
    initialSearchJob.status === PREVIEW_JOB_STATUS.running || initialSearchJob.status === PREVIEW_JOB_STATUS.done
      ? {
          asyncSearchId: initialSearchJob.id,
          nodeId: initialSearchJob.nodeId,
          searchId: initialSearchId,
        }
      : undefined;

  const [mostRecentJobIds, setMostRecentJobIds] = useState<MostRecentJobIds | undefined>(initialSearchJobId);

  useEffect(() => {
    if (
      jobIds &&
      (mostRecentJobIds?.asyncSearchId !== jobIds.asyncSearchId || mostRecentJobIds?.nodeId !== jobIds.nodeId)
    ) {
      setMostRecentJobIds({ ...jobIds, searchId: executedSearchId });
    }
  }, [executedSearchId, jobIds, mostRecentJobIds]);

  return mostRecentJobIds;
};

const resetAlertFlags = () => {
  Store.set(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT, 'false');
  Store.set(HIDE_DL_SEARCH_SUCCESSFUL_SEARCH_ALERT, 'false');
};

type Props<SearchBarFormValues> = {
  filtersFromSearchType: (searchType: BaseLakeSearchType) => SearchBarFormValues;
  initialSearch: Search | undefined;
  initialSearchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  resetUrlQueryParams: () => void;
  widgetFromFormValues: (widget: DataLakeWidget, formValues: SearchBarFormValues) => DataLakeWidget;
  queryFromFormValues: (widget: DataLakeWidget, formValues: SearchBarFormValues) => Query;
  leftCol?: (searchBarFormRef: React.RefObject<FormikProps<SearchBarFormValues>>) => React.ReactNode;
  headerActions?: React.ReactNode;
  searchBar: React.ComponentType<{
    executedSearch: Search | undefined;
    formRef?: React.RefObject<FormikProps<SearchBarFormValues>>;
    onReset: () => void;
    onSubmit: (formValues: SearchBarFormValues) => void;
    searchJobIsRunning: boolean;
  }>;
};

const DataLakePreview = <SearchBarFormValues extends BaseSearchBarFormValues>({
  filtersFromSearchType,
  initialSearch,
  initialSearchJob,
  queryFromFormValues,
  resetUrlQueryParams,
  searchBar: SearchBar,
  widgetFromFormValues,
  headerActions = undefined,
  leftCol = undefined,
}: Props<SearchBarFormValues>) => {
  const dispatch = useDLPreviewDispatch();
  const searchBarFormRef = useRef<FormikProps<SearchBarFormValues>>(null);
  const [executedSearch, setExecutedSearch] = useState<Search | undefined>(
    VALID_INITIAL_SEARCH_STATUSES.includes(initialSearchJob.status) ? initialSearch : undefined,
  );
  const { widgetData, errors, executedAt, duration } = useWidgetResults(executedSearch);
  const mostRecentJobIds = useMostRecentJobIds(initialSearchJob, initialSearch?.id, executedSearch?.id);
  const queryClient = useQueryClient();
  const invalidateDataLakeQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['data-lake'],
    });
    queryClient.invalidateQueries({
      queryKey: [DATA_LAKE_PREVIEW_QUERY_KEY],
    });
  }, [queryClient]);
  const { widget, resetWidget, setWidget } = useWidgetConfiguration();
  const [editing, setEditing] = useState(false);
  const { data: jobProgress, setEnableProgressFetching } = useJobProgress(
    mostRecentJobIds,
    initialSearchJob.status,
    executedSearch?.id,
  );
  const isExecutionFirstJob = useIsLoading();
  const searchJobIsRunning = isExecutionFirstJob || jobProgress?.status === PREVIEW_JOB_STATUS.running;

  usePollResultsForInitialSearchJob(
    initialSearchJob.status,
    initialSearchJob.progress,
    initialSearchJob.id,
    initialSearchJob.nodeId,
  );

  const onChangeWidget = useCallback(
    (newWidget: DataLakeWidget) => {
      setWidget(newWidget);

      return Promise.resolve();
    },
    [setWidget],
  );

  const toggleEdit = useCallback(() => setEditing((cur) => !cur), []);

  const onReset = useCallback(() => {
    setExecutedSearch(undefined);
    resetWidget();
    setEditing(false);
    resetUrlQueryParams();

    return DataLakeQuery.resetActiveQuery().then(() => {
      invalidateDataLakeQueries();

      return dispatch(resetSearchExecutionState());
    });
  }, [dispatch, invalidateDataLakeQueries, resetUrlQueryParams, resetWidget]);

  const onSearchBarSubmit = useCallback(
    async (formValues: SearchBarFormValues) => {
      resetUrlQueryParams();
      resetAlertFlags();

      const updatedWidget = widgetFromFormValues(widget, formValues);
      const query = queryFromFormValues(updatedWidget, formValues);

      const search = await createSearch(Search.create().toBuilder().queries([query]).build()).catch(
        (error: FetchError) => UserNotification.error(`Creating a search failed with status: ${error.message}`),
      );

      if (!search) {
        return Promise.resolve();
      }

      dispatch(resetSearchExecutionState());

      setWidget(updatedWidget);
      setEditing(false);
      setExecutedSearch(search);
      setEnableProgressFetching(true);

      return dispatch(
        execute({
          search,
          activeQuery: search.queries.first().id,
          page: INITIAL_PAGE,
          perPage: PAGE_SIZE,
          stopPolling: (progress) => stopSearchResultsPolling(INITIAL_PAGE, progress),
        }),
      )
        .catch((error: FetchError) =>
          UserNotification.error(`Executing new search failed with status: ${error.message}`),
        )
        .then(() => {
          invalidateDataLakeQueries();
        });
    },
    [
      dispatch,
      invalidateDataLakeQueries,
      queryFromFormValues,
      resetUrlQueryParams,
      setEnableProgressFetching,
      setWidget,
      widget,
      widgetFromFormValues,
    ],
  );

  const messages = useMemo(() => formatBackendMessages(widgetData?.messages), [widgetData?.messages]);
  const hasResult = messages?.length > 0;

  return (
    <>
      <SearchFeatureExplanation />
      {initialSearchJob.status === PREVIEW_JOB_STATUS.expired &&
        initialSearch &&
        !executedSearch &&
        !searchJobIsRunning && (
          <ExpiredSearchInfo<SearchBarFormValues>
            filtersFromSearchType={filtersFromSearchType}
            expiredSearch={initialSearch}
            searchBarFormRef={searchBarFormRef}
          />
        )}
      <Row>
        <Header>
          <LeftHeaderCol>
            <Headline>Data Lake Preview</Headline>
            <IfFeatureEnabled name="external_data_lake_search">
              <DataLakeTypeSelect />
            </IfFeatureEnabled>
          </LeftHeaderCol>
          <RightHeaderCol>
            <ExecutionInfo
              duration={duration}
              executedAt={executedAt}
              executionFinished={executedSearch && !searchJobIsRunning}
              showTotal={false}
            />
            {headerActions}
          </RightHeaderCol>
        </Header>
      </Row>
      <SearchBar
        executedSearch={executedSearch}
        formRef={searchBarFormRef}
        onReset={onReset}
        onSubmit={onSearchBarSubmit}
        searchJobIsRunning={searchJobIsRunning}
      />

      {executedSearch && (
        <>
          {jobProgress?.status === PREVIEW_JOB_STATUS.running && (
            <Delayed delay={initialSearchJob.status === PREVIEW_JOB_STATUS.running ? 0 : 300}>
              <StyledRow>
                <ProgressInformation
                  onCancel={() => {
                    setExecutedSearch(undefined);
                    if (mostRecentJobIds) {
                      return cancelJob(mostRecentJobIds);
                    }

                    return Promise.resolve();
                  }}
                  progress={jobProgress.progress}
                />
              </StyledRow>
            </Delayed>
          )}

          {((initialSearch?.id === executedSearch.id && initialSearchJob?.status === PREVIEW_JOB_STATUS.timeout) ||
            jobProgress?.status === PREVIEW_JOB_STATUS.timeout) && (
            <StyledRow>
              <StyledAlert bsStyle="danger">
                The timeout for this preview search has been reached. Not all messages have been retrieved. Please
                adjust your query parameters in case the current results don’t show what you are looking for.
              </StyledAlert>
            </StyledRow>
          )}

          {errors.length > 0 && (
            <StyledRow>
              <StyledAlert bsStyle="danger">{errors.map((error) => error.description)}</StyledAlert>
            </StyledRow>
          )}

          {errors.length === 0 && (
            <>{jobProgress?.status === PREVIEW_JOB_STATUS.done && <SuccessfulSearchInfo executedAt={executedAt} />}</>
          )}
        </>
      )}

      {!!widget && (
        <FullHeightRow>
          {leftCol ? leftCol(searchBarFormRef) : null}
          <WidgetContainer>
            <DLLogViewWidget
              hasExecutedSearch={!!executedSearch}
              initialPageTotal={widgetData?.total ?? 0}
              mostRecentJobIds={mostRecentJobIds}
              effectiveTimerange={widgetData?.effectiveTimerange}
              messages={messages}
              isFetchingFirstPage={searchJobIsRunning && !hasResult}
              onToggleEdit={toggleEdit}
              onChangeWidget={onChangeWidget}
              widget={widget}
              editing={editing}
            />
          </WidgetContainer>
        </FullHeightRow>
      )}
    </>
  );
};

export default DataLakePreview;
